import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { useActiveClinic } from '../../../context/ClinicContext'
import { useClinicPlanSnapshot } from '../../subscriptions/hooks/useClinicPlanSnapshot'
import { DEFAULT_USER_ID } from '../../../config/appConfig'

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  outline: 'none',
  background: '#fff',
}

const buttonStyle = {
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  background: '#fff',
  cursor: 'pointer',
  fontWeight: 700,
}

function Card({ children, style }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 14,
        padding: 16,
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export default function ClinicsPage() {
  const {
    activeClinicId,
    setActiveClinicId,
  } = useActiveClinic()

  const userId =
    localStorage.getItem('odontoflow_user_id') ||
    DEFAULT_USER_ID

  const [clinics, setClinics] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [settingDefaultId, setSettingDefaultId] = useState(null)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
  })

  const snapshotClinicId =
    activeClinicId && activeClinicId !== 'ALL'
      ? activeClinicId
      : clinics.find((c) => c.isDefault)?.clinicId ||
        clinics[0]?.clinicId ||
        null

  const { data: planSnapshot } = useClinicPlanSnapshot(snapshotClinicId)

  const usage = planSnapshot?.usage || {
    usersCount: 0,
    doctorsCount: 0,
    chairsCount: 0,
    clinicsCount: clinics.length || 0,
  }

  const maxClinics = planSnapshot?.plan?.maxClinics
  const currentPlanName = planSnapshot?.plan?.name || 'Sin plan'

  const clinicsLimitReached =
    maxClinics !== null &&
    maxClinics !== undefined &&
    usage.clinicsCount >= maxClinics

  const loadClinics = useCallback(async () => {
    if (!userId) return

    try {
      setLoading(true)
      setError(null)

      const { data } = await axios.get('/api/multi-clinic/user-clinics', {
        params: { userId },
      })

      setClinics(Array.isArray(data) ? data : [])
    } catch (err) {
      const message =
        err?.response?.data?.message || 'No se pudieron cargar las clínicas'
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    loadClinics()
  }, [loadClinics])

  const defaultClinic = useMemo(() => {
    return clinics.find((item) => item.isDefault) || null
  }, [clinics])

  async function handleCreateClinic() {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      if (!form.name.trim()) {
        setError('El nombre de la clínica es requerido')
        return
      }

      const { data: createdClinic } = await axios.post('/api/multi-clinic/create-clinic', {
        userId,
        name: form.name.trim(),
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        address: form.address.trim() || undefined,
      })

      const createdClinicId = createdClinic?.id || createdClinic?.clinicId

      if (createdClinicId) {
        localStorage.setItem('odontoflow_clinic_id', createdClinicId)
        setActiveClinicId?.(createdClinicId)
      }

      setForm({
        name: '',
        phone: '',
        email: '',
        address: '',
      })

      await loadClinics()
      window.dispatchEvent(new Event('odontoflow:clinics-updated'))
      setSuccess('Clínica creada correctamente.')
    } catch (err) {
      const message =
        err?.response?.data?.message || 'No se pudo crear la clínica'
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSaving(false)
    }
  }

  async function handleSetDefault(clinicId) {
    try {
      setSettingDefaultId(clinicId)
      setError(null)
      setSuccess(null)

      await axios.patch('/api/multi-clinic/set-default', {
        userId,
        clinicId,
      })

      localStorage.setItem('odontoflow_clinic_id', clinicId)
      if (setActiveClinicId) {
        setActiveClinicId(clinicId)
      }

      await loadClinics()
      window.dispatchEvent(new Event('odontoflow:clinics-updated'))
      setSuccess('Clínica por defecto actualizada.')
    } catch (err) {
      const message =
        err?.response?.data?.message || 'No se pudo actualizar la clínica por defecto'
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSettingDefaultId(null)
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, marginBottom: 6 }}>Clínicas</h2>
        <div style={{ color: '#64748b' }}>
          Crea, visualiza y administra tus clínicas sin enredar el arranque.
        </div>
      </div>

      {error && (
        <div
          style={{
            background: '#fff1f0',
            border: '1px solid #ffa39e',
            color: '#a8071a',
            borderRadius: 10,
            padding: '10px 12px',
            marginBottom: 16,
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            color: '#237804',
            borderRadius: 10,
            padding: '10px 12px',
            marginBottom: 16,
            fontWeight: 600,
          }}
        >
          {success}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Card>
          <div
            style={{
              fontWeight: 800,
              color: '#0f172a',
              marginBottom: 12,
            }}
          >
            Uso actual de tu cuenta
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
              fontSize: 14,
              color: '#334155',
            }}
          >
            <div>🏥 Clínicas: {usage.clinicsCount}</div>
            <div>👤 Usuarios: {usage.usersCount}</div>
            <div>🦷 Doctores: {usage.doctorsCount}</div>
            <div>🪑 Sillas: {usage.chairsCount}</div>
          </div>

          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              color: '#64748b',
            }}
          >
            Plan actual: <strong>{currentPlanName}</strong>
            {maxClinics !== null && maxClinics !== undefined ? (
              <>
                {' '}· Límite de clínicas: <strong>{maxClinics}</strong>
              </>
            ) : (
              <>
                {' '}· Límite de clínicas: <strong>Ilimitado</strong>
              </>
            )}
          </div>

          {clinicsLimitReached && (
            <div
              style={{
                marginTop: 12,
                background: '#fff7e6',
                border: '1px solid #ffd591',
                color: '#874d00',
                borderRadius: 10,
                padding: '10px 12px',
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              Tu cuenta ya llegó al límite de clínicas para este plan.
            </div>
          )}
        </Card>

        <Card>
          <div
            style={{
              fontWeight: 800,
              color: '#0f172a',
              marginBottom: 12,
            }}
          >
            Nueva clínica
          </div>

          <div
            style={{
              display: 'grid',
              gap: 10,
            }}
          >
            <input
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Nombre de la clínica"
              style={inputStyle}
            />

            <input
              value={form.phone}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, phone: e.target.value }))
              }
              placeholder="Teléfono"
              style={inputStyle}
            />

            <input
              value={form.email}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="Correo"
              style={inputStyle}
            />

            <input
              value={form.address}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, address: e.target.value }))
              }
              placeholder="Dirección"
              style={inputStyle}
            />

            <button
              onClick={handleCreateClinic}
              disabled={saving || clinicsLimitReached}
              style={{
                ...buttonStyle,
                background: saving || clinicsLimitReached ? '#cbd5e1' : '#2563eb',
                color: '#fff',
                border: 'none',
                cursor: saving || clinicsLimitReached ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Creando...' : 'Crear clínica'}
            </button>
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 18 }}>
        <Card>
          <div
            style={{
              fontWeight: 800,
              color: '#0f172a',
              marginBottom: 14,
            }}
          >
            Mis clínicas
          </div>

          {loading ? (
            <div style={{ color: '#64748b' }}>Cargando clínicas...</div>
          ) : clinics.length === 0 ? (
            <div style={{ color: '#64748b' }}>
              No hay clínicas registradas todavía.
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gap: 12,
              }}
            >
              {clinics.map((item) => {
                const clinic = item.clinic || {}
                const isCurrent = activeClinicId === item.clinicId
                const isDefault = !!item.isDefault

                return (
                  <div
                    key={item.id}
                    style={{
                      border: isCurrent ? '2px solid #2563eb' : '1px solid #e5e7eb',
                      borderRadius: 12,
                      padding: 14,
                      background: '#fff',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        alignItems: 'start',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 800,
                            color: '#0f172a',
                          }}
                        >
                          {clinic.name || 'Clínica'}
                        </div>

                        <div
                          style={{
                            color: '#64748b',
                            marginTop: 4,
                            fontSize: 14,
                            display: 'grid',
                            gap: 4,
                          }}
                        >
                          <div>📞 {clinic.phone || 'Sin teléfono'}</div>
                          <div>✉️ {clinic.email || 'Sin correo'}</div>
                          <div>📍 {clinic.address || 'Sin dirección'}</div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          gap: 8,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                        }}
                      >
                        {isDefault && (
                          <span
                            style={{
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              border: '1px solid #bfdbfe',
                              borderRadius: 999,
                              padding: '5px 9px',
                              fontSize: 12,
                              fontWeight: 800,
                            }}
                          >
                            Por defecto
                          </span>
                        )}

                        {isCurrent && (
                          <span
                            style={{
                              background: '#f0fdf4',
                              color: '#166534',
                              border: '1px solid #bbf7d0',
                              borderRadius: 999,
                              padding: '5px 9px',
                              fontSize: 12,
                              fontWeight: 800,
                            }}
                          >
                            Activa
                          </span>
                        )}

                        {!isDefault && (
                          <button
                            onClick={() => handleSetDefault(item.clinicId)}
                            disabled={settingDefaultId === item.clinicId}
                            style={{
                              ...buttonStyle,
                              background: '#f8fafc',
                              cursor:
                                settingDefaultId === item.clinicId
                                  ? 'not-allowed'
                                  : 'pointer',
                            }}
                          >
                            {settingDefaultId === item.clinicId
                              ? 'Guardando...'
                              : 'Usar por defecto'}
                          </button>
                        )}

                        {!isCurrent && (
                          <button
                            onClick={() => {
                              localStorage.setItem('odontoflow_clinic_id', item.clinicId)
                              if (setActiveClinicId) {
                                setActiveClinicId(item.clinicId)
                              }
                              setSuccess(`Clínica activa cambiada a ${clinic.name}.`)
                            }}
                            style={{
                              ...buttonStyle,
                              background: '#eef2ff',
                              border: '1px solid #c7d2fe',
                            }}
                          >
                            Activar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {defaultClinic && (
            <div
              style={{
                marginTop: 14,
                paddingTop: 14,
                borderTop: '1px solid #e5e7eb',
                color: '#64748b',
                fontSize: 13,
              }}
            >
              Clínica por defecto actual: <strong>{defaultClinic.clinic?.name}</strong>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}